import React, { FC } from 'react';
import { Row, Col, CardDeck, Card, CardBody } from 'reactstrap';
import { fetcher } from '@/libs/fetcher/fetcher';
import useSWR from 'swr';
import classnames from 'classnames';
import styles from './TopPicks.module.scss';
import Link from 'next/link';
import TruncateMarkup from 'react-truncate-markup';
import parse from 'html-react-parser';
import { useTranslations } from 'next-intl';
import { assetPath } from '@/libs/assetPath/assetPath';

//import components
import GridImages from '@/components/GridImages/GridImages';
import { useEnvContext } from '@/context/envContext';
import getBTURL from '@/libs/getBTURL/getBTURL';

// Libraries
import { sanitizeTitle } from '@/libs/sanitizeTitle/sanitizeTitle';
import { sitenameImageIndexHandler } from '@/libs/sitenameImageIndexHandler/sitenameImageIndexHandler';
import useResFallbackImages from '@/libs/resFallbackImages/resFallbackImages';
import { pushGTMClickTags } from '@/libs/pushGTMTags/pushGTMTags';
interface TopPicksProps {
  resId: string;
  orgId: string;
  sectionHeadingTitle: string;
  validationParams?: {
    customerLinks: boolean;
    leadDropValidation: boolean;
    peDemandSub: boolean;
  };
  utmParams?: string;
  lang?: string;
}
interface TopPickAssetProps {
  resId: string;
  title: string;
  docType: string;
  thumbnail: string;
  primarySiteName?: string;
}
const TopPicks: FC<TopPicksProps> = ({
  orgId,
  sectionHeadingTitle,
  utmParams,
  lang
}) => {
  const apiUrl = `/hub/services/orgRecommendation?orgId=${orgId}&lang=${lang}`;
  const {
    data,
    error
  } = useSWR(apiUrl, fetcher);
  const Data: Array<TopPickAssetProps> = data?.body?.resources;
  const t = useTranslations('Asset');
  const fallbackImages = useResFallbackImages();
  const sitenameImageIndex: Record<string, number> = {};
  const {
    webUrl,
    cdnEnv
  } = useEnvContext();
  function topPicksUrlsTracking(utmParams: string | undefined) {
    // If no persistent urls, then just return asrc=FF-
    if (!utmParams) {
      return '?asrc=FF-';
    }

    // check if asrc=FF- is in the persistent URL.
    let persistentUrl = utmParams.includes('asrc=FF-');
    if (persistentUrl === true) {
      return utmParams;
    } else if (utmParams) {
      return utmParams + '&asrc=FF-';
    }
  }
  if (error) {
    return;
  }
  if (!Data) {
    return;
  }
  const getAssetURL = (resId: string, docType: string) => {
    if (docType == 'Webinar' || resId.includes('-')) {
      return 'https://' + getBTURL(webUrl) + '/webcast/' + resId.replace('-', '/') + topPicksUrlsTracking(utmParams);
    } else {
      return assetPath({
        resId: resId,
        locale: lang
      }) + topPicksUrlsTracking(utmParams);
    }
  };
  return <>
   {sectionHeadingTitle && <Row className={'gx-'}>
     <h2 className={classnames(styles['top-picks-title'], 'section-heading')} data-bdd={'section-heading_top-picks'}>
      {sectionHeadingTitle}
     </h2>
    </Row>}

   <Row className={classnames(styles['toppicks-row'], 'g-3')} data-bdd="toppicks-row" data-sentry-element="Row" data-sentry-source-file="TopPicks.tsx">
    {Data?.map(({
        resId,
        title,
        docType,
        primarySiteName,
        thumbnail
      }, index) => resId && <Col sm="12" md="6" lg="4" key={`toppicksitem-` + resId + index}>
        <CardDeck>
         <Card className={styles['toppicks-card']}>
          <Link onClick={() => pushGTMClickTags({})} className={classnames('stretched-link', styles['link-img-hover'])} href={getAssetURL(resId, docType)} prefetch={false} target={docType == 'Webinar' || resId.includes('-') ? '_blank' : '_self'}>
           <span aria-hidden="true"></span>
          </Link>

          <span aria-hidden="true" className={classnames(styles['cardindex-image'], styles['cardindex-' + index])} style={{
              backgroundImage: `url(${cdnEnv}/rms/images/icons/toppicks-corner-flash-${index}.svg)`
            }}></span>
          <div className={classnames('grid-image-wrap', styles['toppicks-image-wrap'])}>
           {docType == 'Webinar' || resId.includes('-') ? <GridImages GridType={'webinar'} resId={resId} thumbnail={thumbnail} primarySiteName={primarySiteName ? primarySiteName : 'genericMulti'} fallbackimage={sitenameImageIndexHandler(sitenameImageIndex, fallbackImages, primarySiteName ? primarySiteName : 'genericMulti')} /> : <GridImages GridType={'toppicks'} resId={resId} primarySiteName={primarySiteName ? primarySiteName : 'genericMulti'} fallbackimage={sitenameImageIndexHandler(sitenameImageIndex, fallbackImages, primarySiteName ? primarySiteName : 'genericMulti')} />}
          </div>

          <CardBody className={styles['toppicks-card-body']}>
           <p className={styles['asset-type']}>{docType}</p>

           {typeof title === 'string' && title.length > 0 && <TruncateMarkup lines={1} tokenize={'words'}>
             <h3 className={styles['card-title']}>
              {parse(sanitizeTitle(title))}
             </h3>
            </TruncateMarkup>}
          </CardBody>
         </Card>
        </CardDeck>
       </Col>)}
    <p className={styles['see-more-resources-link']}>
     <Link onClick={() => pushGTMClickTags({
          click_element: 'Anchor',
          click_closest_class: 'see-more-resources-link',
          click_section_ID: 'NONE',
          link_classes: 'text-links-body-color',
          link_id: 'NONE',
          link_text: 'See more resources',
          link_url: `${lang != 'en' ? '/' + lang : ''}/${orgId}${utmParams}`,
          click_category: 'NONE',
          click_destination: 'Org resources',
          click_closest_href: 'NONE'
        })} className={'text-links-body-color'} href={`${lang != 'en' ? '/' + lang : ''}/${orgId}${utmParams}`} data-sentry-element="Link" data-sentry-source-file="TopPicks.tsx">
      {t('SeeMoreResources')}
     </Link>
    </p>
   </Row>
  </>;
};
export default TopPicks;